import React from 'react';
import type { EFilingTab } from '../contexts/EFilingContext';
import { useBrand } from '../../../app/config/brand';

interface WizardHeroProps {
  currentTab: EFilingTab;
}

const WizardHero: React.FC<WizardHeroProps> = ({ currentTab }) => {
  const brand = useBrand();
  const isFirstStep = currentTab === 'documents';

  return (
    <div
      className="px-8 transition-all duration-300"
      style={{
        background: `linear-gradient(135deg, ${brand.colors.primaryLight} 0%, ${brand.colors.bgLight} 60%, ${brand.colors.bgWhite} 100%)`,
        paddingTop: isFirstStep ? '2.5rem' : '1.25rem',
        paddingBottom: isFirstStep ? '2rem' : '1.25rem',
      }}
    >
      <h1
        className="font-bold transition-all duration-300"
        style={{
          fontSize: isFirstStep ? '1.75rem' : '1.25rem',
          color: brand.colors.secondary,
          fontFamily: brand.typography.fontFamily.heading,
          lineHeight: 1.3,
        }}
      >
        {isFirstStep
          ? "Let's file your court documents"
          : 'eFiling — New Order'}
      </h1>

      {/* Subtitle and meta chips only on first step */}
      {isFirstStep && (
        <div className="mt-3">
          <p
            className="text-sm leading-relaxed max-w-xl"
            style={{ color: brand.colors.textLight }}
          >
            Upload your filing — we'll read it and pre-fill court, parties, and attorneys
            (we'll ask for the specific case type if we can't read it).
            Most people finish in about three minutes.
          </p>

          <div className="flex items-center gap-3 mt-4">
            <MetaChip icon="clock" label="~3 min total" />
            <MetaChip icon="dollar" label="Est. $51.75" />
            <MetaChip icon="shield" label="Court-accepted" />
          </div>
        </div>
      )}
    </div>
  );
};

interface MetaChipProps {
  icon: 'clock' | 'dollar' | 'shield';
  label: string;
}

const MetaChip: React.FC<MetaChipProps> = ({ icon, label }) => {
  const brand = useBrand();
  const iconSvg = {
    clock: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    dollar: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    shield: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
  };

  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
      style={{
        backgroundColor: `${brand.colors.primary}14`,
        color: brand.colors.link,
      }}
    >
      {iconSvg[icon]}
      {label}
    </span>
  );
};

export default WizardHero;
